import { jsPDF } from 'jspdf';
import raeLogo from '../assets/images/RAE-LOGO.png';
import type { CapacityPlan } from '../types';
import type { PlanMetrics } from './capacityPlan';

// Load the RAE logo as a data URL + intrinsic size so it can be embedded in the
// PDF header. Cached after the first call. Resolves to null if it can't load
// (the export then falls back to the text-only header).
let logoCache: { dataUrl: string; width: number; height: number } | null | undefined;
function loadLogo(): Promise<{ dataUrl: string; width: number; height: number } | null> {
  if (logoCache !== undefined) return Promise.resolve(logoCache);
  return new Promise(resolve => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) { logoCache = null; resolve(null); return; }
        ctx.drawImage(img, 0, 0);
        logoCache = { dataUrl: canvas.toDataURL('image/png'), width: img.naturalWidth, height: img.naturalHeight };
        resolve(logoCache);
      } catch {
        logoCache = null;
        resolve(null);
      }
    };
    img.onerror = () => { logoCache = null; resolve(null); };
    img.src = raeLogo;
  });
}

interface ZoneStat {
  code: string;
  name: string;
  total: number;
  finished: number;
  atRisk: number;
  estimatedHours: number;
  actualHours: number;
  variance: number;
  progress: number;
}

interface TechStat {
  id: string;
  name: string;
  total: number;
  finished: number;
  active: number;
  overdue: number;
  issues: number;
  completion: number;
  estimatedHours: number;
  actualHours: number;
}

interface ReportExportData {
  progress: number;
  allSubsLength: number;
  // Delivery-health metrics shown in the Reports hero strip.
  onTimeRate: number;
  overdue: number;
  throughput7d: number;
  // Only rendered when time tracking is enabled (matches the on-screen view).
  timeTracking: boolean;
  totalEst: number;
  totalActual: number;
  zoneStats: ZoneStat[];
  techStats: TechStat[];
}

function fh(hours: number): string {
  if (hours === 0) return '0h';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function drawTable(
  doc: jsPDF,
  headers: string[],
  rows: (string | number)[][],
  startY: number,
  colWidths: number[],
  marginLeft: number,
): number {
  const rowH = 7;
  const headerH = 8;
  const totalW = colWidths.reduce((a, b) => a + b, 0);
  // Header: RAE blue band, white text.
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setFillColor(40, 80, 158);
  doc.setTextColor(255, 255, 255);
  doc.rect(marginLeft, startY, totalW, headerH, 'F');
  let x = marginLeft;
  headers.forEach((h, i) => {
    doc.text(h, x + 2, startY + 5.5);
    x += colWidths[i];
  });

  // Body: dark ink on white, light-gray zebra striping.
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(40, 44, 55);
  let y = startY + headerH;
  rows.forEach((row, ri) => {
    if (ri % 2 === 0) {
      doc.setFillColor(244, 246, 250);
      doc.rect(marginLeft, y, totalW, rowH, 'F');
    }
    x = marginLeft;
    row.forEach((cell, ci) => {
      doc.text(String(cell), x + 2, y + 5);
      x += colWidths[ci];
    });
    y += rowH;
  });
  // Outer border around the table.
  doc.setDrawColor(224, 228, 236);
  doc.rect(marginLeft, startY, totalW, headerH + rows.length * rowH, 'S');
  return y + 4;
}

export async function exportReportPdf(data: ReportExportData, projectName: string): Promise<void> {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const W = 297;
  const margin = 14;
  let y = 15;

  // Light theme: white page, dark ink.
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, W, 210, 'F');

  const logo = await loadLogo();
  let titleX = margin;
  if (logo) {
    const logoH = 10;
    const logoW = (logo.width / logo.height) * logoH;
    // Logo sits directly on the white page — no plate needed.
    doc.addImage(logo.dataUrl, 'PNG', margin, y - 8, logoW, logoH);
    titleX = margin + logoW + 4;
  }

  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(40, 80, 158); // RAE blue
  doc.text('RAE Project Management', titleX, y);
  y += 8;

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(60, 60, 70);
  doc.text(`Project: ${projectName}`, margin, y);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, W - margin, y, { align: 'right' });
  y += 3;

  doc.setDrawColor(210, 214, 224);
  doc.line(margin, y, W - margin, y);
  y += 6;

  // KPI boxes — mirror the on-screen Reports health strip.
  const variance = data.totalActual - data.totalEst;
  type Kpi = { label: string; value: string; tone?: 'blue' | 'red' | 'green' };
  const kpis: Kpi[] = [
    { label: 'Progress', value: `${data.progress.toFixed(1)}%` },
    { label: 'On-time Rate', value: `${Math.round(data.onTimeRate)}%` },
    { label: 'Overdue', value: String(data.overdue), tone: data.overdue > 0 ? 'red' : 'green' },
    { label: 'Throughput (7d)', value: String(data.throughput7d) },
    { label: 'Total Tasks', value: String(data.allSubsLength) },
  ];
  if (data.timeTracking) {
    kpis.push(
      { label: 'Est. Hours', value: fh(data.totalEst) },
      { label: 'Actual Hours', value: fh(data.totalActual) },
      { label: 'Variance', value: `${variance >= 0 ? '+' : ''}${fh(variance)}`, tone: variance > 0 ? 'red' : 'green' },
    );
  }
  const gap = 4;
  const boxW = (W - margin * 2 - gap * (kpis.length - 1)) / kpis.length;
  kpis.forEach((kpi, i) => {
    const bx = margin + i * (boxW + gap);
    doc.setFillColor(244, 246, 250);
    doc.setDrawColor(224, 228, 236);
    doc.roundedRect(bx, y, boxW, 18, 3, 3, 'FD');
    doc.setFontSize(kpis.length > 5 ? 11 : 13);
    doc.setFont('helvetica', 'bold');
    if (kpi.tone === 'red') doc.setTextColor(200, 50, 50);
    else if (kpi.tone === 'green') doc.setTextColor(30, 130, 70);
    else doc.setTextColor(40, 80, 158);
    doc.text(kpi.value, bx + boxW / 2, y + 9, { align: 'center' });
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(110, 116, 130);
    doc.text(kpi.label, bx + boxW / 2, y + 15, { align: 'center' });
  });
  y += 24;

  // Zone Performance table
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(40, 80, 158);
  doc.text('Zone Performance', margin, y);
  y += 5;

  // Zone Performance — hours columns only when time tracking is on, matching the view.
  const zoneColWidths = data.timeTracking
    ? [22, 70, 16, 18, 16, 22, 22, 22, 20]
    : [26, 110, 20, 22, 20, 24];
  const zoneHeaders = data.timeTracking
    ? ['Code', 'Zone Name', 'Tasks', 'Finished', 'At risk', 'Est. Hours', 'Act. Hours', 'Variance', 'Progress']
    : ['Code', 'Zone Name', 'Tasks', 'Finished', 'At risk', 'Progress'];
  const zoneRows = data.zoneStats.map(z => {
    const name = z.name.length > 35 ? z.name.slice(0, 34) + '…' : z.name;
    return data.timeTracking
      ? [z.code, name, z.total, z.finished, z.atRisk, fh(z.estimatedHours), fh(z.actualHours), `${z.variance >= 0 ? '+' : ''}${fh(z.variance)}`, `${z.progress.toFixed(0)}%`]
      : [z.code, name, z.total, z.finished, z.atRisk, `${z.progress.toFixed(0)}%`];
  });
  y = drawTable(doc, zoneHeaders, zoneRows, y, zoneColWidths, margin);

  // Team Performance table (if data present)
  if (data.techStats.length > 0) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(40, 80, 158);
    doc.text('Team Performance', margin, y);
    y += 5;

    const techColWidths = data.timeTracking
      ? [56, 16, 18, 16, 18, 16, 26, 26, 22]
      : [70, 20, 20, 20, 22, 20, 24];
    const techHeaders = data.timeTracking
      ? ['Name', 'Tasks', 'Done', 'Active', 'Overdue', 'Issues', 'Est. Hours', 'Act. Hours', 'Completion']
      : ['Name', 'Tasks', 'Done', 'Active', 'Overdue', 'Issues', 'Completion'];
    const techRows = data.techStats.map(t => {
      const name = t.name.length > 28 ? t.name.slice(0, 27) + '…' : t.name;
      return data.timeTracking
        ? [name, t.total, t.finished, t.active, t.overdue, t.issues, fh(t.estimatedHours), fh(t.actualHours), `${t.completion.toFixed(0)}%`]
        : [name, t.total, t.finished, t.active, t.overdue, t.issues, `${t.completion.toFixed(0)}%`];
    });
    drawTable(doc, techHeaders, techRows, y, techColWidths, margin);
  }

  const safeProjectName = projectName.replace(/\s+/g, '_').replace(/[^\w-]/g, '');
  doc.save(`RAE_Report_${safeProjectName}_${new Date().toISOString().slice(0, 10)}.pdf`);
}

export function exportReportCsv(data: ReportExportData, projectName: string): void {
  const tt = data.timeTracking;
  const rows: string[] = [
    `Project,${projectName}`,
    `Date,${new Date().toLocaleDateString()}`,
    `Progress,${data.progress.toFixed(1)}%`,
    `On-time Rate,${data.onTimeRate.toFixed(0)}%`,
    `Overdue,${data.overdue}`,
    `Throughput (7d),${data.throughput7d}`,
    `Total Tasks,${data.allSubsLength}`,
    ...(tt ? [
      `Est. Hours,${data.totalEst.toFixed(1)}`,
      `Actual Hours,${data.totalActual.toFixed(1)}`,
      `Variance,${(data.totalActual - data.totalEst).toFixed(1)}`,
    ] : []),
    '',
    'ZONE PERFORMANCE',
    tt
      ? 'Zone Code,Zone Name,Tasks,Finished,At Risk,Est. Hours,Actual Hours,Variance,Progress%'
      : 'Zone Code,Zone Name,Tasks,Finished,At Risk,Progress%',
    ...data.zoneStats.map(z => (tt
      ? [z.code, `"${z.name}"`, z.total, z.finished, z.atRisk, z.estimatedHours.toFixed(1), z.actualHours.toFixed(1), z.variance.toFixed(1), z.progress.toFixed(1)]
      : [z.code, `"${z.name}"`, z.total, z.finished, z.atRisk, z.progress.toFixed(1)]).join(',')
    ),
    '',
    'TEAM PERFORMANCE',
    tt
      ? 'Name,Tasks,Done,Active,Overdue,Issues,Est. Hours,Actual Hours,Completion%'
      : 'Name,Tasks,Done,Active,Overdue,Issues,Completion%',
    ...data.techStats.map(t => (tt
      ? [`"${t.name}"`, t.total, t.finished, t.active, t.overdue, t.issues, t.estimatedHours.toFixed(1), t.actualHours.toFixed(1), t.completion.toFixed(1)]
      : [`"${t.name}"`, t.total, t.finished, t.active, t.overdue, t.issues, t.completion.toFixed(1)]).join(',')
    ),
  ];
  const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `RAE_Report_${projectName.replace(/\s+/g, '_').replace(/[^\w-]/g, '')}_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ==================== Capacity Plan export ====================

export async function exportPlanPdf(
  plan: CapacityPlan,
  metrics: PlanMetrics,
  labels: string[],
  projectName: string,
): Promise<void> {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const W = 297;
  const margin = 14;
  let y = 15;

  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, W, 210, 'F');

  const logo = await loadLogo();
  let titleX = margin;
  if (logo) {
    const logoH = 10;
    const logoW = (logo.width / logo.height) * logoH;
    doc.addImage(logo.dataUrl, 'PNG', margin, y - 8, logoW, logoH);
    titleX = margin + logoW + 4;
  }
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(40, 80, 158);
  doc.text('RAE Capacity Plan', titleX, y);
  y += 8;

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(60, 60, 70);
  doc.text(`Project: ${projectName}`, margin, y);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, W - margin, y, { align: 'right' });
  y += 3;
  doc.setDrawColor(210, 214, 224);
  doc.line(margin, y, W - margin, y);
  y += 6;

  // Parameter + summary KPIs
  const kpis = [
    { label: 'Hours / Shift', value: String(plan.params.hoursPerShift) },
    { label: 'Days / Week', value: String(plan.params.daysPerWeek) },
    { label: 'Active Shifts', value: String(plan.params.activeShifts) },
    { label: 'Weeks', value: String(plan.weekCount) },
    { label: 'Peak Week', value: String(metrics.peakWeekHeadcount) },
    { label: 'Total Man-Hours', value: `${Math.round(metrics.totalManHours).toLocaleString()}` },
  ];
  const gap = 4;
  const boxW = (W - margin * 2 - gap * (kpis.length - 1)) / kpis.length;
  kpis.forEach((kpi, i) => {
    const bx = margin + i * (boxW + gap);
    doc.setFillColor(244, 246, 250);
    doc.setDrawColor(224, 228, 236);
    doc.roundedRect(bx, y, boxW, 18, 3, 3, 'FD');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(40, 80, 158);
    doc.text(kpi.value, bx + boxW / 2, y + 9, { align: 'center' });
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(110, 116, 130);
    doc.text(kpi.label, bx + boxW / 2, y + 15, { align: 'center' });
  });
  y += 24;

  // Staffing man-hours table (discipline x week)
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(40, 80, 158);
  doc.text('Man-Hours by Discipline', margin, y);
  y += 5;

  const nameW = 40;
  const totalW = 26;
  const weekW = Math.max(10, (W - margin * 2 - nameW - totalW) / Math.max(labels.length, 1));
  const colWidths = [nameW, ...labels.map(() => weekW), totalW];
  const headers = ['Discipline', ...labels, 'Total'];
  const rows: (string | number)[][] = metrics.byDiscipline.map(d => [
    d.label,
    ...d.perWeek.map(v => Math.round(v).toLocaleString()),
    Math.round(d.total).toLocaleString(),
  ]);
  rows.push(['Weekly Total', ...metrics.weeklyManHours.map(v => Math.round(v).toLocaleString()), Math.round(metrics.totalManHours).toLocaleString()]);
  rows.push(['Cumulative', ...metrics.cumulativeManHours.map(v => Math.round(v).toLocaleString()), '']);
  drawTable(doc, headers, rows, y, colWidths, margin);

  const safe = projectName.replace(/\s+/g, '_').replace(/[^\w-]/g, '');
  doc.save(`RAE_CapacityPlan_${safe}_${new Date().toISOString().slice(0, 10)}.pdf`);
}

export function exportPlanCsv(
  plan: CapacityPlan,
  metrics: PlanMetrics,
  labels: string[],
  projectName: string,
): void {
  const rows: string[] = [
    `Project,${projectName}`,
    `Date,${new Date().toLocaleDateString()}`,
    `Hours per Shift,${plan.params.hoursPerShift}`,
    `Days per Week,${plan.params.daysPerWeek}`,
    `Active Shifts,${plan.params.activeShifts}`,
    `Shift Multiplier,${plan.params.shiftMultiplier}`,
    `Weeks,${plan.weekCount}`,
    `Total Man-Hours,${Math.round(metrics.totalManHours)}`,
    '',
    'HEADCOUNT BY DISCIPLINE',
    `Discipline,${labels.join(',')},Total`,
    ...metrics.byDiscipline.map(d =>
      [d.label, ...plan.staffing[d.key], plan.staffing[d.key].reduce((a, b) => a + b, 0)].join(',')
    ),
    '',
    'MAN-HOURS BY DISCIPLINE',
    `Discipline,${labels.join(',')},Total`,
    ...metrics.byDiscipline.map(d =>
      [d.label, ...d.perWeek.map(v => Math.round(v)), Math.round(d.total)].join(',')
    ),
    `Weekly Total,${metrics.weeklyManHours.map(v => Math.round(v)).join(',')},${Math.round(metrics.totalManHours)}`,
    `Cumulative,${metrics.cumulativeManHours.map(v => Math.round(v)).join(',')},`,
  ];
  const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `RAE_CapacityPlan_${projectName.replace(/\s+/g, '_').replace(/[^\w-]/g, '')}_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
